import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../../config/api';
import { useNavigate } from 'react-router-dom';
import { FaFilter, FaCalendar, FaMapMarkerAlt } from 'react-icons/fa';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import SearchBar from '../../../../components/SearchBar.jsx';
import EventImage from '../../../../components/EventImage';
import { DEFAULT_IMAGE_DATA_URI, fixS3ImageUrl } from '../../../../components/imageUtils';
import '../css/EventMenu.css';

const getBackgroundStyle = (rawBackground) => {
  if (!rawBackground) {
    return {};
  }

  let background = rawBackground;

  // Allow background to be either a real array, JSON-stringified array,
  // or a Postgres text[] string like {image,"https://..."}
  if (!Array.isArray(background) && typeof background === 'string') {
    const trimmed = background.trim();

    // First, try JSON.parse (e.g. '["image","https://..."]')
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        background = parsed;
      }
    } catch {
      // If it's not JSON, handle Postgres array syntax
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const inner = trimmed.slice(1, -1);
        const parts = inner.split(',');
        if (parts.length > 0) {
          background = parts.map((p) => {
            const t = p.trim();
            if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
              return t.slice(1, -1);
            }
            return t;
          });
        }
      }
    }
  }

  if (!Array.isArray(background) || background.length === 0) {
    return {};
  }

  const mode = background[0];
  const value1 = background[1];
  const value2 = background[2];

  if (mode === 'solid' && value1) {
    return {
      backgroundColor: value1,
      backgroundImage: 'none',
    };
  }

  if (mode === 'gradient' && value1 && value2) {
    return {
      backgroundImage: `linear-gradient(90deg, ${value1}, ${value2})`,
      backgroundColor: 'transparent',
    };
  }

  if (mode === 'image' && value1) {
    const fixedBgUrl = fixS3ImageUrl(value1);
    return {
      backgroundImage: `url(${fixedBgUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'transparent',
    };
  }

  return {};
};

const EventMenu = () => {
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');

  const eventTypes = [
    'Conference',
    'Workshop',
    'Seminar',
    'Exhibition',
    'Concert',
    'Festival',
    'Corporate Event',
    'Sports Event',
  ];

  // Generate dynamic date options
  const getDateOptions = () => {
    const today = new Date();
    const oneMonthFromNow = new Date(today);
    oneMonthFromNow.setMonth(today.getMonth() + 1);
    
    const threeMonthsFromNow = new Date(today);
    threeMonthsFromNow.setMonth(today.getMonth() + 3);
    
    const oneYearFromNow = new Date(today);
    oneYearFromNow.setFullYear(today.getFullYear() + 1);

    return [
      { value: '', label: 'All Dates' },
      { value: '1month', label: `Within 1 Month (${today.toLocaleDateString()} - ${oneMonthFromNow.toLocaleDateString()})` },
      { value: '3months', label: `Within 3 Months (${today.toLocaleDateString()} - ${threeMonthsFromNow.toLocaleDateString()})` },
      { value: '1year', label: `Within 1 Year (${today.toLocaleDateString()} - ${oneYearFromNow.toLocaleDateString()})` },
      { value: 'custom', label: 'Custom Date Range' }
    ];
  };

  const dateOptions = getDateOptions();

  useEffect(() => {
    if (!events.length) return;
    filterEvents(searchQuery, selectedEventType, dateFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, selectedEventType, dateFilter]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const timestamp = new Date().getTime();
        const response = await fetch(
          `${API_BASE_URL}/shared/events/available?_=${timestamp}`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch events: ${response.statusText}`);
        }

        const data = await response.json();

        const eventsWithImageInfo = Array.isArray(data)
          ? data.map((event) => {
              const rawStartDate = event.startdate || event.date || '';
              const dateString =
                typeof rawStartDate === 'string'
                  ? rawStartDate.slice(0, 10)
                  : rawStartDate instanceof Date
                  ? rawStartDate.toISOString().slice(0, 10)
                  : '';

              const capacityRaw = event.capacity ?? event.total_tickets_planned ?? 0;
              const soldRaw = event.sold_tickets ?? 0;
              const capacity = Number.isFinite(Number(capacityRaw)) ? Number(capacityRaw) : 0;
              const sold = Number.isFinite(Number(soldRaw)) ? Number(soldRaw) : 0;
              const available = capacity > 0 ? Math.max(0, capacity - sold) : 0;

              return {
                ...event,
                // Normalise id to match customer view routing
                id: event.id || event.event_id || event.EventID || event.eventid,
                name: event.name || event.event_name || event.title || 'Unnamed Event',
                location: event.location || 'Location not specified',
                capacity,
                sold_tickets: sold,
                available_capacity: available,
                // Use startdate from Events table as the card date
                date: dateString,
                // Normalised image and background fields to match Landing EventCard behaviour
                file_url:
                  event.file_url ||
                  event.coverimage ||
                  event.cover_image_url ||
                  DEFAULT_IMAGE_DATA_URI,
                background: event.background_images_array || event.background || null,
              };
            })
          : [];

        setEvents(eventsWithImageInfo);
        setFilteredEvents(eventsWithImageInfo);
      } catch (err) {
        console.error('Error fetching events:', err);
        setError(err.message);

        const fallbackEvents = [
          {
            id: 999,
            name: 'Fallback Event (API Error)',
            location: 'Error Recovery Mode',
            date: 'Today',
            time: 'Now',
            file_url: DEFAULT_IMAGE_DATA_URI,
            link: '/customerviewevent/999',
            description:
              'This is a fallback event shown because the API request failed.',
            capacity: 100,
            event_type: 'Error',
          },
        ];

        setEvents(fallbackEvents);
        setFilteredEvents(fallbackEvents);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const filterEvents = (
    query = '',
    eventType = selectedEventType,
    date = dateFilter
  ) => {
    let filtered = [...events];

    if (query) {
      const searchTerms = query.toLowerCase().trim().split(' ');
      filtered = filtered.filter((event) => {
        const searchString = `${event.name || ''} ${event.location || ''} ${
          event.description || ''
        } ${event.event_type || ''}`.toLowerCase();
        return searchTerms.every((term) => searchString.includes(term));
      });
    }

    if (eventType && eventType !== 'all') {
      filtered = filtered.filter(
        (event) =>
          event.event_type?.toLowerCase() === eventType.toLowerCase()
      );
    }

    if (date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (date === '1month') {
        const oneMonthFromNow = new Date(today);
        oneMonthFromNow.setMonth(today.getMonth() + 1);
        filtered = filtered.filter((event) => {
          if (!event.date) return false;
          const eventDate = new Date(event.date);
          return eventDate >= today && eventDate <= oneMonthFromNow;
        });
      } else if (date === '3months') {
        const threeMonthsFromNow = new Date(today);
        threeMonthsFromNow.setMonth(today.getMonth() + 3);
        filtered = filtered.filter((event) => {
          if (!event.date) return false;
          const eventDate = new Date(event.date);
          return eventDate >= today && eventDate <= threeMonthsFromNow;
        });
      } else if (date === '1year') {
        const oneYearFromNow = new Date(today);
        oneYearFromNow.setFullYear(today.getFullYear() + 1);
        filtered = filtered.filter((event) => {
          if (!event.date) return false;
          const eventDate = new Date(event.date);
          return eventDate >= today && eventDate <= oneYearFromNow;
        });
      } else if (date === 'custom' && customFromDate && customToDate) {
        const fromDate = new Date(customFromDate);
        const toDate = new Date(customToDate);
        filtered = filtered.filter((event) => {
          if (!event.date) return false;
          const eventDate = new Date(event.date);
          return eventDate >= fromDate && eventDate <= toDate;
        });
      } else {
        // For backward compatibility - treat as exact date match
        filtered = filtered.filter((event) => event.date?.includes(date));
      }
    }

    setFilteredEvents(filtered);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedEventType('all');
    setDateFilter('');
    setShowCustomDates(false);
    setCustomFromDate('');
    setCustomToDate('');
    filterEvents('', 'all', '');
  };

  // Re-filter when custom dates change
  useEffect(() => {
    if (dateFilter === 'custom' && customFromDate && customToDate) {
      filterEvents(searchQuery, selectedEventType, dateFilter);
    }
  }, [customFromDate, customToDate]);

  const hasActiveFilters = searchQuery || selectedEventType !== 'all' || dateFilter;

  return (
    <div className="event-menu">
      <GlobalHeader
        title="Browse Events"
        subtitle="Find and book tickets for upcoming events"
        backTo="/customerdash"
        logoutTo="/"
      />

      <main className="event-menu__main">
        <section className="event-menu__search-bar">
          <div className="event-menu__search-wrapper">
            <SearchBar
              searchTerm={searchQuery}
              setSearchTerm={setSearchQuery}
              onSearch={(term) => filterEvents(term, selectedEventType, dateFilter)}
            />
            <button
              type="button"
              className="event-menu__filter-button-inline"
              onClick={() => setShowFilters(true)}
            >
              <FaFilter /> Filters
            </button>
          </div>
        </section>

        {loading ? (
          <div className="event-menu__message-container">
            <p>Loading events...</p>
          </div>
        ) : error ? (
          <div className="event-menu__message-container">
            <p>Error loading events: {error}</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="event-menu__message-container">
            <p>
              No events match your search criteria. Please try different
              filters.
            </p>
          </div>
        ) : (
          <div className="event-menu__grid">
            {filteredEvents.map((event) => {
              const bgStyle = getBackgroundStyle(event.background);

              return (
                <div
                  key={event.id}
                  className="event-card"
                  onClick={() => nav(`/customerviewevent/${event.id}`)}
                >
                  <div
                    className="event-card__image-container"
                    style={bgStyle}
                  >
                    <EventImage
                      image={event.file_url}
                      coverimage={event.file_url}
                      alt={event.name}
                      className="event-card__image"
                      loading="eager"
                    />
                  </div>
                  <div className="event-card__details">
                    <h3 className="event-card__title">{event.name}</h3>

                    <div className="event-card__info">
                      <FaMapMarkerAlt className="event-card__info-icon" />
                      <span>{event.location || 'TBA'}</span>
                    </div>

                    <div className="event-card__info">
                      <FaCalendar className="event-card__info-icon" />
                      <span>{event.date || 'TBA'}</span>
                    </div>

                    {event.event_type && (
                      <div className="event-card__tags">
                        <span className="event-card__tag">{event.event_type}</span>
                        {event.capacity ? (
                          event.available_capacity > 0 ? (
                            <span className="event-card__tag event-card__tag--available">
                              {event.available_capacity.toLocaleString()} seats left
                            </span>
                          ) : (
                            <span className="event-card__tag event-card__tag--soldout">
                              Sold out
                            </span>
                          )
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showFilters && (
          <div
            className="event-menu__overlay"
            onClick={() => setShowFilters(false)}
          >
            <div
              className="event-menu__filter-popup"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="event-menu__filter-close"
                onClick={() => setShowFilters(false)}
              >
                ✕
              </button>

              <h2 className="event-menu__filter-title">Filters</h2>

              <div className="event-menu__filter-section">
                <div className="event-menu__filter-group">
                  <label className="event-menu__filter-label">Date</label>
                  <select
                    className="event-menu__filter-select"
                    value={dateFilter}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDateFilter(value);
                      setShowCustomDates(value === 'custom');
                    }}
                  >
                    {dateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {showCustomDates && (
                    <div style={{ marginTop: 15 }}>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', marginBottom: 5, fontSize: '14px', fontWeight: '500' }}>
                          From Date:
                        </label>
                        <input
                          type="date"
                          className="event-menu__filter-date"
                          value={customFromDate}
                          onChange={(e) => setCustomFromDate(e.target.value)}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 5, fontSize: '14px', fontWeight: '500' }}>
                          To Date:
                        </label>
                        <input
                          type="date"
                          className="event-menu__filter-date"
                          value={customToDate}
                          onChange={(e) => setCustomToDate(e.target.value)}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="event-menu__filter-group">
                  <label className="event-menu__filter-label">Event Type</label>
                  <select
                    className="event-menu__filter-select"
                    value={selectedEventType}
                    onChange={(e) => setSelectedEventType(e.target.value)}
                  >
                    <option value="all">All Event Types</option>
                    {eventTypes.map((type) => (
                      <option key={type} value={type.toLowerCase()}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="event-menu__filter-buttons">
                <button
                  type="button"
                  className="event-menu__filter-apply"
                  onClick={() => setShowFilters(false)}
                >
                  Apply Filters
                </button>
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="event-menu__filter-clear"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default EventMenu;
